<template>
  <li
    :id="item.id"
    class="i-tree-item"
    :class="expandable ? 'i-tree-item-parent' : 'i-tree-item-leaf'"
    role="treeitem"
    v-bind="{
      ...(expandable && {'aria-expanded': expanded}),
    }"
  >
    <div
      class="i-tree-item-row"
      :class="{ 'selected': selected }"
      @mouseover="hover = true"
      @mouseleave="hover = false"
      @click.prevent="$emit('select', item)"
      @keydown.enter.prevent="$emit('select', item)"
      ref="itemRow"
      :tabindex="focusable ? 0 : -1"
      @keydown.up.prevent="$emit('navUp', item)"
      @keydown.down.prevent="$emit('navDown', item)"
      @keydown.right.prevent="$emit('navRight', item)"
      @keydown.left.prevent="$emit('navLeft', item)"
    >
      <div v-for="l in level" :key="l" class="i-tree-item-indent"></div>
      <div
        class="i-tree-item-toggle"
        :style="{visibility: expandable ? 'visible' : 'hidden'}"
        @click.prevent.stop="$emit('toggle', item, { expandDescendants: $event.shiftKey })"
      >
        <i-icon
          v-if="status === 'loading'"
          icon="spinner"
          class="fa-spin fa-fw"
        />
        <i-icon
          v-else
          :icon="expanded ? 'chevron-down' : 'chevron-right'"
          class="fa-fw"
        />
      </div>
      <slot
        v-bind="{
          item,
          expanded,
          expandable,
          level,
          hover,
          selected,
          status,
          expandItem: (opts) => $emit('expand', item, opts),
          collapseItem: (opts) => $emit('collapse', item, opts),
          toggleItem: (opts) => $emit('toggle', item, opts),
        }"
      >
        {{ item.name }}
      </slot>
    </div>
    <i-tree
      v-if="expandable && expanded"
      :items="item.children"
      :level="level+1"
      v-slot="scope"
    >
      <slot v-bind="scope" />
    </i-tree>
  </li>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import Icon from '@/components/lib/icon/Icon.vue';

const asyncTree = defineAsyncComponent(() => import('./Tree.vue'));

export default {
  name: 'i-tree-item',
  components: {
    'i-tree': asyncTree,
    [Icon.name]: Icon,
  },
  props: {
    item: {
      type: Object,
    },
    level: {
      type: Number,
    },
    expanded: {
      type: Boolean,
    },
    selected: {
      type: Boolean,
    },
    expandable: {
      type: Boolean,
      default: false,
    },
    focusable: {
      type: Boolean,
    },
    status: {
      type: String,
      default: 'idle',
      validator(value) {
        return ['idle', 'loading', 'resolved', 'error'].includes(value);
      },
    },
  },
  data: () => ({
    hover: false,
  }),
  mounted() {
    if (this.selected) {
      this.$refs.itemRow.scrollIntoView();
    }
  },
  watch: {
    focusable(value) {
      const { itemRow } = this.$refs;
      if (value && itemRow) itemRow.focus();
    },
  },
};

</script>

<style scoped lang='scss'>
.i-tree-item-row {
  display: flex;
  align-items: center;
}
.i-tree-item-indent {
  display: inline-block;
  width: 15px;
  min-width: 15px;
}
.i-tree-item-toggle {
  cursor: pointer;
  padding: 0 5px;
}
</style>
